import React, { useEffect, useState, useCallback } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Download } from "lucide-react";
import { formatCents } from "@/lib/format";
import { toast } from "sonner";

function Stat({ label, value, tone, sub }) {
  return (
    <div className="border rounded-2xl bg-white p-5">
      <p className="text-xs font-bold tracking-[0.12em] uppercase text-ink-muted">{label}</p>
      <p className={`font-num font-bold text-2xl mt-2 ${tone === "overdue" ? "text-status-danger" : tone === "success" ? "text-status-success" : "text-midnight"}`}>{value}</p>
      {sub != null && <p className="text-xs text-ink-muted mt-1">{sub}</p>}
    </div>
  );
}

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData((await api.get("/reports")).data); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/export.csv`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "paysignal-records.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-24 rounded-lg" /><Skeleton className="h-64 rounded-lg" /></div>;
  if (error) return <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded-lg p-6">{error}</div>;

  const chart = data.by_source.slice(0, 8).map((s) => ({ name: s.name, value: s.amount_cents / 100 }));
  const months = (data.paid_by_month || []).map((m) => ({
    name: new Date(m.month + "-01T00:00:00").toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
    value: m.amount_cents / 100,
  }));

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Reports</h1>
          <p className="text-espresso-soft mt-1">A record-keeping snapshot of your paid and outstanding activity.</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="reports-export"><Download size={16} className="mr-1" /> Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Paid this month" value={data.paid_month} tone="success" />
        <Stat label="Paid YTD" value={data.paid_ytd} tone="success" />
        <Stat label="Outstanding invoices" value={data.outstanding} />
        <Stat label="Overdue invoices" value={data.overdue} tone="overdue" sub={`${data.overdue_count} invoice${data.overdue_count !== 1 ? "s" : ""}`} />
        <Stat label="Pending payouts" value={data.pending_payouts} sub={`${data.pending_count} payout${data.pending_count !== 1 ? "s" : ""}`} />
      </div>

      <div className="border rounded-2xl bg-white p-6" data-testid="reports-by-month">
        <h2 className="font-heading font-bold mb-4">Paid by month</h2>
        {months.length === 0 ? (
          <p className="text-sm text-ink-muted">No paid records yet.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ left: 10, right: 10 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip formatter={(v) => formatCents(v * 100, data.currency)} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {months.map((_, i) => <Cell key={i} fill="#4F7CFF" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="border rounded-2xl bg-white p-6" data-testid="reports-by-source">
        <h2 className="font-heading font-bold mb-4">Paid totals by client / platform</h2>
        {chart.length === 0 ? (
          <p className="text-sm text-ink-muted">No paid records yet this year.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} layout="vertical" margin={{ left: 20, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatCents(v * 100, data.currency)} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chart.map((_, i) => <Cell key={i} fill="#0F172A" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <p className="text-xs text-ink-muted border-t pt-4" data-testid="reports-disclaimer">
        Reports are provided for record-keeping purposes and are not tax or accounting advice.
      </p>
    </div>
  );
}
